import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { ROUTES } from '@shared/config/routes';
import { updatePassword } from '@shared/services/authService';
import { KeyRound, Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * ChangePasswordPage
 *
 * Shown on first login when `requires_password_change` is true.
 * Forces the user to set a new password before accessing the app.
 */
const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword]     = useState('');
  const [confirm, setConfirm]             = useState('');
  const [showNew, setShowNew]             = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [done, setDone]                   = useState(false);

  const strength = (() => {
    if (newPassword.length === 0) return null;
    if (newPassword.length < 8)   return 'weak';
    const hasUpper  = /[A-Z]/.test(newPassword);
    const hasLower  = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSymbol = /[^A-Za-z0-9]/.test(newPassword);
    const score = [hasUpper, hasLower, hasNumber, hasSymbol].filter(Boolean).length;
    if (score <= 2) return 'fair';
    if (score === 3) return 'good';
    return 'strong';
  })();

  const strengthConfig = {
    weak:   { label: 'Weak',   bar: 'bg-red-500',    width: 'w-1/4' },
    fair:   { label: 'Fair',   bar: 'bg-amber-500',  width: 'w-2/4' },
    good:   { label: 'Good',   bar: 'bg-blue-500',   width: 'w-3/4' },
    strong: { label: 'Strong', bar: 'bg-green-500',  width: 'w-full' },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await updatePassword(newPassword);
      setDone(true);
      // Brief pause so user sees the success message, then redirect
      setTimeout(() => navigate(ROUTES.APP.HOME, { replace: true }), 1800);
    } catch (err) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / branding area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
            <KeyRound className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Set Your Password</h1>
          <p className="text-sm text-muted-foreground mt-1">
            You must choose a new password before continuing.
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-sm p-6 space-y-5">
          {done ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-14 h-14 rounded-full bg-green-100 border border-green-300 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-green-600" />
              </div>
              <p className="text-base font-semibold text-foreground">Password updated!</p>
              <p className="text-sm text-muted-foreground">Taking you to the app…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Info banner */}
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 font-medium">
                Your account was set up with a temporary password. Please choose a personal password now.
              </div>

              {/* New password */}
              <div className="space-y-1.5">
                <Label htmlFor="new-pw" className="text-xs text-muted-foreground">
                  New Password *
                </Label>
                <div className="relative">
                  <Input
                    id="new-pw"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Choose a strong password"
                    className="pr-10"
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Strength bar */}
                {strength && (
                  <div className="space-y-1">
                    <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${strengthConfig[strength].bar} ${strengthConfig[strength].width}`} />
                    </div>
                    <p className={`text-[11px] font-medium ${
                      strength === 'weak' ? 'text-red-500' :
                      strength === 'fair' ? 'text-amber-500' :
                      strength === 'good' ? 'text-blue-500' : 'text-green-600'
                    }`}>
                      {strengthConfig[strength].label} password
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm */}
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw" className="text-xs text-muted-foreground">
                  Confirm Password *
                </Label>
                <div className="relative">
                  <Input
                    id="confirm-pw"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirm && newPassword && confirm !== newPassword && (
                  <p className="text-[11px] text-red-500 font-medium">Passwords don't match</p>
                )}
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-red-600 font-medium rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                  {error}
                </p>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</>
                ) : (
                  'Set New Password'
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
